import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { inputCls } from "@/components/Filters";
import { BoxApprovateDaLiquidare } from "@/components/BoxApprovateDaLiquidare";
import { dateIt, eurShort, num } from "@/lib/format";

const BOX = [
  { key: "pratiche", titolo: "Estratto completo pratiche",
    nota: "Fonte principale: estratto completo delle pratiche con importi finanziati. Aggiorna dashboard, scostamenti, monitoraggio, classifiche e archivi." },
  { key: "premi", titolo: "Premi assicurativi",
    nota: "Premi CPI, CVT e NFK per numero pratica: aggiorna i tachimetri dei premi e l'archivio premi. Colonne accettate: PRATICA + IMPORTO_CPI/CVT/NFK oppure TIPO_PREMIO + IMPORTO_PREMIO." },
  { key: "dealers", titolo: "Convenzionati",
    nota: "Anagrafica dei convenzionati: ragione sociale, commerciale, settore, indirizzo. Aggiorna mappa, classifiche e archivio convenzionati." },
  { key: "giornaliero", titolo: "Pratiche giornaliere",
    nota: "Fogli PRT (caricato e liquidato) o file giornaliero ELENCOPRA: aggiornano solo la pagina Pratiche giornaliere, senza toccare dashboard, classifiche e archivi." },
];

export default function Importazioni() {
  const [target, setTarget] = useState("giornaliero");
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState([]);
  const fileRefs = useRef({});

  const [future, setFuture] = useState(null);
  const [check, setCheck] = useState(null);
  const [correggendo, setCorreggendo] = useState(false);

  const [nomiScelti, setNomiScelti] = useState([]);
  const aggiornaNomi = async () => {
    try {
      const r = await api.post(`/dealers/aggiorna-nomi?token=${preview.token}`, { codici: nomiScelti });
      toast.success(`${r.data.aggiornati.length} ragioni sociali aggiornate in anagrafica`);
      setNomiScelti([]);
      setPreview({ ...preview, nomi_diversi: [] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const [annullando, setAnnullando] = useState(null);
  const annulla = async (b) => {
    const avviso = `Annullare l'importazione "${b.filename}"?\n\n`
      + "Verranno eliminati i record creati da questo file e ripristinati i valori precedenti "
      + "di quelli modificati.\nAttenzione: se dopo questa importazione ne sono state fatte "
      + "altre sugli stessi dati, l'annullamento può riportare indietro valori più recenti.";
    if (!window.confirm(avviso)) return;
    setAnnullando(b.id);
    try {
      const r = await api.post(`/import/batches/${b.id}/annulla`);
      toast.success(`Importazione annullata · ${r.data.eliminati} record eliminati, `
        + `${r.data.ripristinati} ripristinati`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    setAnnullando(null);
  };

  const load = () => api.get("/import/batches").then((r) => setBatches(r.data.rows)).catch(() => setBatches([]));
  const loadFuture = () => api.get("/manutenzione/pratiche-future")
    .then((r) => setFuture(r.data)).catch(() => setFuture(null));
  const loadCheck = () => api.get("/manutenzione/verifica-archivio")
    .then((r) => setCheck(r.data)).catch(() => setCheck(null));
  useEffect(() => { load(); loadFuture(); loadCheck(); }, []);

  const correggi = async () => {
    if (!window.confirm("Correggere le marcature degli estratti, le date future e le pratiche "
      + "giornaliere finite per errore nell'archivio?\n\nNessuna pratica di produzione verrà "
      + "eliminata; i dati del 2025 sono congelati e non vengono toccati.")) return;
    setCorreggendo(true);
    try {
      const { data } = await api.post("/manutenzione/verifica-archivio");
      toast.success(`Archivio corretto: ${data.marcature_corrette} marcature, `
        + `${data.data_futura_rimosse + data.giornaliere_rimosse} pratiche anomale rimosse`);
      loadCheck(); loadFuture();
    } catch (e) { toast.error(apiError(e)); } finally { setCorreggendo(false); }
  };

  const rimuoviFuture = async () => {
    try {
      const { data } = await api.delete("/manutenzione/pratiche-future");
      toast.success(`${data.eliminate} pratiche con data futura rimosse`);
      loadFuture();
    } catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (e, chiave) => {
    e.preventDefault();
    const file = fileRefs.current[chiave]?.files?.[0];
    if (!file) return toast.error("Seleziona un file");
    setTarget(chiave);
    setBusy(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/preview?target=${chiave}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`Anteprima pronta: ${data.righe_valide} righe valide`);
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };

  const [confermaPremi, setConfermaPremi] = useState(false);
  const [premiAnomali, setPremiAnomali] = useState(null);
  const loadPremi = () => api.get("/premi-anomali").then((r) => setPremiAnomali(r.data)).catch(() => setPremiAnomali(null));
  useEffect(() => { loadPremi(); }, []);
  const correggiPremio = async (r) => {
    const v = window.prompt(`Pratica ${r.pratica} · ${r.campo} attuale ${num(r.valore, 2)} €.\n`
      + "Inserisci il premio corretto (vuoto = azzera il premio):", "");
    if (v === null) return;
    const valore = v.trim() ? Number(v.replace(/\./g, "").replace(",", ".")) : null;
    if (v.trim() && Number.isNaN(valore)) return toast.error("Importo non valido");
    try {
      await api.put(`/premi-anomali/${encodeURIComponent(r.pratica)}?campo=${r.campo}${valore ? `&valore=${valore}` : ""}`);
      toast.success(`Pratica ${r.pratica}: ${r.campo} ${valore ? `corretto in ${num(valore, 2)} €` : "azzerato"}`);
      loadPremi();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Correzione non riuscita");
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/import/commit?token=${preview.token}${confermaPremi ? "&conferma_premi=true" : ""}`);
      setConfermaPremi(false);
      loadPremi();
      setReport(data); setPreview(null); load();
      toast.success("Importazione completata");
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };

  const commitSostituzione = async () => {
    const anno = Number((preview.periodo_a || "").slice(0, 4));
    const azione = preview.tipo_estratto === "liquidato"
      ? "azzererà la liquidazione delle pratiche dell'anno assenti dal file"
      : "eliminerà dall'archivio le pratiche dell'anno assenti dal file";
    if (!window.confirm(`ATTENZIONE · sostituzione integrale dell'anno ${anno}.\n\n`
      + `Questa operazione ${azione}.\n\nProcedi solo se il file è l'estratto completo YTD `
      + `ufficiale della banca. L'operazione è annullabile dall'elenco importazioni.`)) return;
    const parola = window.prompt(`Per confermare la sostituzione integrale del ${anno} `
      + `scrivi SOSTITUISCI (tutto maiuscolo)`);
    if ((parola || "").trim().toUpperCase() !== "SOSTITUISCI") {
      return toast.error("Sostituzione annullata: parola di conferma non corretta");
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/import/commit-sostituzione?token=${preview.token}`
        + `&anno=${anno}&conferma=SOSTITUISCI`);
      setReport(data); setPreview(null); load(); loadCheck();
      toast.success(`Anno ${anno} sostituito · ${data.pratiche_rimosse} pratiche rimosse, `
        + `${data.liquidazioni_azzerate} liquidazioni azzerate`);
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };

  return (
    <div data-testid="importazioni-page">
      <SectionTitle sub="Quattro flussi separati: ogni file aggiorna dashboard, pratiche giornaliere, premi o convenzionati. In caso di doppioni del numero pratica vale sempre l'ultimo file caricato, e gli archivi si aggiornano di conseguenza.">
        Importazioni
      </SectionTitle>

      <Card className="p-6 mb-8" data-testid="verifica-archivio-box">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
              Verifica archivio
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Controlla marcature, date future e giornaliere finite in archivio. I dati del 2025 sono
              congelati: nessun import li modifica.
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={loadCheck} data-testid="btn-verifica-archivio"
              className="rounded-md border border-slate-200 px-4 py-2 text-sm text-slate-600
                hover:border-slate-400 transition-colors duration-200">Ricontrolla</button>
            <button type="button" onClick={correggi} disabled={correggendo || !check?.totale}
              data-testid="btn-correggi-archivio"
              className="rounded-md bg-[#0018A8] px-4 py-2 text-sm font-semibold text-white
                hover:bg-[#001480] disabled:opacity-40 transition-colors duration-200">
              {correggendo ? "Correzione…" : "Correggi archivio"}
            </button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="verifica-anomalie">
          {(check?.anomalie || []).map((a) => (
            <div key={a.chiave} data-testid={`anomalia-${a.chiave}`}
              className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm
                ${a.conteggio ? "border-amber-300 bg-amber-50/60 text-amber-900"
                  : "border-emerald-200 bg-emerald-50/50 text-emerald-800"}`}>
              <span className="font-mono font-semibold w-12 text-right">{num(a.conteggio)}</span>
              <span className="flex-1">{a.label}</span>
            </div>
          ))}
          {check && !check.totale && (
            <div className="text-sm text-emerald-700 md:col-span-2">
              Archivio allineato agli estratti: nessuna anomalia.
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6" data-testid="premi-anomali-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="uppercase text-xs tracking-[0.18em] font-bold text-red-700">
              Premi assicurativi oltre {num(premiAnomali?.soglia || 15000)} €
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Pratiche con un premio CPI/CVT/NFK/GGN sospetto (tipico errore di punti e virgole).
              Correggi o azzera il premio: la modifica vale per archivio e giornaliere.
            </p>
          </div>
          <button type="button" onClick={loadPremi} data-testid="btn-ricarica-premi-anomali"
            className="rounded-md border border-slate-200 px-4 py-2 text-sm text-slate-600
              hover:border-slate-400 transition-colors duration-200">Ricontrolla</button>
        </div>
        {(premiAnomali?.rows || []).length === 0 ? (
          <div className="mt-4 text-sm text-emerald-700">Nessun premio oltre soglia.</div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2">Pratica</th><th className="px-3 py-2">Dealer</th><th className="px-3 py-2">Caricata</th>
                  <th className="px-3 py-2">Liquidata</th><th className="px-3 py-2">Premio</th>
                  <th className="px-3 py-2 text-right">Valore</th><th className="px-3 py-2 text-right">Finanziato</th><th></th></tr>
              </thead>
              <tbody>
                {premiAnomali.rows.map((r) => (
                  <tr key={`${r.pratica}-${r.campo}`} className="border-t border-slate-100" data-testid={`premio-anomalo-${r.pratica}`}>
                    <td className="px-3 py-2 font-mono">{r.pratica}</td>
                    <td className="px-3 py-2">{r.dealer}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.data_caricamento || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.data_liquidazione || "—"}</td>
                    <td className="px-3 py-2">{r.campo}</td>
                    <td className="px-3 py-2 font-mono text-right text-red-700 font-semibold">{num(r.valore, 2)} €</td>
                    <td className="px-3 py-2 font-mono text-right">{r.importo_finanziato ? `${num(r.importo_finanziato, 2)} €` : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" onClick={() => correggiPremio(r)} data-testid={`btn-correggi-premio-${r.pratica}`}
                        className="rounded-md bg-[#0018A8] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#001480]">
                        Correggi
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {!!future?.totale && (
        <Card className="p-6 mb-8 border-amber-300 bg-amber-50/70" data-testid="pratiche-future-box">
          <div className="uppercase text-xs tracking-[0.18em] font-semibold text-amber-700">
            Controllo qualità · pratiche con data di caricamento futura
          </div>
          <p className="text-sm text-amber-900 mt-2">
            {num(future.totale)} pratiche hanno una data di caricamento successiva a oggi
            ({dateIt(future.oggi)}): non possono esistere in ELENCOPRA e falsano la data di
            aggiornamento e le proiezioni.
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs" data-testid="pratiche-future-table">
              <thead><tr className="text-amber-700 uppercase text-[10px] tracking-wider text-left">
                <th className="py-1 pr-4">Pratica</th><th className="py-1 pr-4">Data caricamento</th>
                <th className="py-1 pr-4">Convenzionato</th><th className="py-1 pr-4">Stato</th>
              </tr></thead>
              <tbody>
                {future.rows.map((r) => (
                  <tr key={r.numero_pratica} className="border-t border-amber-200">
                    <td className="py-1 pr-4 font-mono">{r.numero_pratica}</td>
                    <td className="py-1 pr-4 font-mono">{dateIt(r.data_caricamento)}</td>
                    <td className="py-1 pr-4">{r.dealer_nome || r.dealer_codice}</td>
                    <td className="py-1 pr-4">{r.stato}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={rimuoviFuture} data-testid="btn-rimuovi-pratiche-future"
            className="mt-4 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white
              hover:bg-amber-700 transition-colors duration-200">
            Rimuovi queste pratiche
          </button>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-testid="import-box">
        {BOX.map((b) => (
          <Card key={b.key} className="p-6" data-testid={`import-box-${b.key}`}>
            <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
              {b.titolo}
            </div>
            <p className="text-sm text-slate-500 mt-2 mb-2 min-h-[64px]">{b.nota}</p>
            <p className="text-[11px] text-slate-400 mb-3">Formati accettati: XLSX, XLS e CSV
              (separatore ; oppure ,)</p>
            <form onSubmit={(e) => upload(e, b.key)} className="flex flex-wrap items-center gap-3">
              <input type="file" accept=".xlsx,.xls,.csv,.txt" className="text-sm"
                data-testid={`import-file-${b.key}`}
                ref={(el) => { fileRefs.current[b.key] = el; }} />
              <button type="submit" disabled={busy} data-testid={`import-preview-${b.key}`}
                className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium
                  hover:bg-slate-800 disabled:opacity-60 transition-colors duration-200">
                {busy && target === b.key ? "Elaborazione…" : "Genera anteprima"}
              </button>
            </form>
          </Card>
        ))}
        <BoxApprovateDaLiquidare />
      </div>

      {preview && (
        <Card className="p-6 mb-8" data-testid="import-preview">
          <h3 className="font-semibold">Anteprima · {preview.filename}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
            <Stat label="Righe totali" value={preview.totale_righe} />
            <Stat label="Righe valide" value={preview.righe_valide} />
            <Stat label="Righe scartate" value={preview.righe_scartate} />
            <Stat label="Colonne mappate" value={Object.keys(preview.mapping).length} />
          </div>
          {preview.target === "pratiche" && (
            <div className="mt-4 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md
              px-3 py-2" data-testid="preview-estratto">
              Tipo riconosciuto: <b>{(preview.tipo_estratto || "non riconosciuto").toUpperCase()}</b>
              {preview.periodo_da && (
                <> · periodo del file <b>{dateIt(preview.periodo_da)} → {dateIt(preview.periodo_a)}</b>
                  {" "}({preview.giorni_coperti} giorni) ·{" "}
                  <b>{preview.completo ? "estratto completo YTD" : "aggiornamento parziale"}</b>
                </>
              )}
              {preview.periodo_da && (
                <div className="text-xs text-slate-500 mt-1">
                  I dati si sommano all'archivio esistente: le pratiche già presenti vengono aggiornate,
                  quelle assenti dal file restano invariate. Le pratiche del 2025 sono congelate e vengono ignorate.
                </div>
              )}
            </div>
          )}
          {(preview.nomi_diversi || []).length > 0 && (
            <div className="mt-4 rounded-md border border-sky-300 bg-sky-50/70 p-3" data-testid="preview-nomi-diversi">
              <p className="text-sm text-sky-900">
                {preview.nomi_diversi.length} convenzionati hanno il codice già in archivio ma una
                <b> ragione sociale diversa</b> nel file. Vuoi aggiornare il nome in anagrafica?
              </p>
              <div className="mt-2 max-h-56 overflow-y-auto space-y-1">
                {preview.nomi_diversi.map((n) => (
                  <label key={n.codice_dealer} className="flex items-start gap-2 text-xs text-slate-700"
                    data-testid={`nome-diverso-${n.codice_dealer}`}>
                    <input type="checkbox" className="mt-0.5" checked={nomiScelti.includes(n.codice_dealer)}
                      onChange={(e) => setNomiScelti((v) => e.target.checked
                        ? [...v, n.codice_dealer] : v.filter((c) => c !== n.codice_dealer))} />
                    <span>
                      <span className="font-mono">{n.codice_dealer}</span> ·{" "}
                      <span className="text-slate-500 line-through">{n.nome_archivio}</span> →{" "}
                      <b>{n.nome_file}</b> <span className="text-slate-400">({n.righe} righe)</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <button type="button" data-testid="btn-aggiorna-nomi" disabled={!nomiScelti.length}
                  onClick={aggiornaNomi}
                  className="px-3 py-1.5 rounded-md bg-sky-700 text-white text-sm hover:bg-sky-800
                    disabled:opacity-40 transition-colors duration-200">
                  Aggiorna i nomi selezionati ({nomiScelti.length})
                </button>
                <button type="button" data-testid="btn-seleziona-tutti-nomi"
                  onClick={() => setNomiScelti(preview.nomi_diversi.map((n) => n.codice_dealer))}
                  className="px-3 py-1.5 rounded-md border border-sky-300 text-sky-800 text-sm
                    hover:bg-sky-100 transition-colors duration-200">
                  Seleziona tutti
                </button>
              </div>
            </div>
          )}
          {preview.stornate > 0 && (
            <div className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
              data-testid="preview-stornate">
              {preview.stornate} pratiche risultano <b>stornate/revocate</b> in questo file: il loro
              importo verrà escluso dal liquidato e dalle proiezioni, restando nel caricato.
            </div>
          )}
          <div className="mt-5 text-xs text-slate-500">
            <b>Mapping riconosciuto:</b> {Object.entries(preview.mapping).map(([k, v]) => `${k} → ${v}`).join(" · ")}
          </div>
          {preview.colonne_non_mappate.length > 0 && (
            <div className="mt-2 text-xs text-amber-700">Colonne ignorate: {preview.colonne_non_mappate.join(", ")}</div>
          )}
          {preview.errori.length > 0 && (
            <div className="mt-5">
              <div className="text-sm font-medium text-red-600 mb-2">Errori di validazione (prime 50 righe)</div>
              <div className="max-h-56 overflow-y-auto border border-slate-200 rounded" data-testid="import-errors">
                <table className="w-full text-xs">
                  <tbody>
                    {preview.errori.map((e, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="px-3 py-1.5 font-mono">Riga {e.riga}</td>
                        <td className="px-3 py-1.5 text-red-600">{(e.errori || [e.errore]).filter(Boolean).join("; ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {(preview.premi_anomali || []).length > 0 && (
            <div className="mt-5 rounded-md border-2 border-red-300 bg-red-50 px-4 py-3" data-testid="preview-premi-anomali">
              <div className="text-sm font-semibold text-red-800">
                {preview.premi_anomali.length} premi assicurativi superano {num(preview.soglia_premio)} €: verifica punti e virgole
              </div>
              <div className="mt-2 max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {preview.premi_anomali.map((a, i) => (
                      <tr key={i} className="border-b border-red-100">
                        <td className="px-2 py-1 font-mono">Riga {a.riga}</td>
                        <td className="px-2 py-1 font-mono">{a.pratica}</td>
                        <td className="px-2 py-1">{a.campo}</td>
                        <td className="px-2 py-1 font-mono text-right text-red-700 font-semibold">{num(a.valore, 2)} €</td>
                        <td className="px-2 py-1 text-slate-500">finanziato {a.importo_finanziato ? `${num(a.importo_finanziato, 2)} €` : "n/d"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-red-900">
                <input type="checkbox" checked={confermaPremi} onChange={(e) => setConfermaPremi(e.target.checked)}
                  data-testid="conferma-premi-checkbox" />
                Confermo che questi importi sono corretti e voglio importarli
              </label>
            </div>
          )}
          <button onClick={commit} data-testid="import-commit-btn"
            disabled={busy || preview.righe_valide === 0 || ((preview.premi_anomali || []).length > 0 && !confermaPremi)}
            className="mt-6 bg-emerald-600 text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-emerald-700 transition-colors duration-200 disabled:opacity-60">
            Conferma importazione ({preview.righe_valide} righe)
          </button>
          {preview.target === "pratiche" && preview.completo && (
            <div className="mt-8 pt-6 border-t border-dashed border-red-300" data-testid="sostituzione-box">
              <div className="uppercase text-xs tracking-[0.18em] font-semibold text-red-700">
                Zona rischiosa · sostituzione integrale dell'anno
              </div>
              <p className="text-sm text-slate-600 mt-2 max-w-3xl">
                Azione separata e volontaria: oltre ad aggiornare le pratiche del file,
                {preview.tipo_estratto === "liquidato"
                  ? " azzera la liquidazione delle pratiche dell'anno che non compaiono più nell'estratto."
                  : " elimina dall'archivio le pratiche dell'anno che non compaiono più nell'estratto."}
                {" "}Usala solo con l'estratto completo YTD ufficiale della banca. Resta annullabile
                dall'elenco importazioni.
              </p>
              <button onClick={commitSostituzione} disabled={busy}
                data-testid="import-sostituzione-btn"
                className="mt-4 border border-red-300 text-red-700 bg-white rounded-md px-5 py-2 text-sm font-medium hover:bg-red-50 transition-colors duration-200 disabled:opacity-60">
                Sostituisci integralmente l'anno {(preview.periodo_a || "").slice(0, 4)}
              </button>
            </div>
          )}
        </Card>
      )}

      {report && (
        <Card className="p-6 mb-8 border-emerald-200 bg-emerald-50/50" data-testid="import-report">
          <h3 className="font-semibold text-emerald-800">Report importazione</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
            <Stat label="Righe totali" value={report.totale_righe} />
            <Stat label="Inserite" value={report.righe_importate} />
            <Stat label="Aggiornate" value={report.righe_aggiornate} />
            <Stat label="Scartate" value={report.righe_scartate} />
          </div>
          {report.sostituzione_integrale && (
            <div className="mt-4 text-sm text-red-700 bg-white border border-red-200 rounded-md px-3 py-2"
              data-testid="report-sostituzione">
              Sostituzione integrale dell'anno <b>{report.anno_sostituito}</b> ·{" "}
              <b>{report.pratiche_rimosse}</b> pratiche rimosse ·{" "}
              <b>{report.liquidazioni_azzerate}</b> liquidazioni azzerate
            </div>
          )}
        </Card>
      )}

      {report?.codici_sconosciuti?.length > 0 && (
        <Card className="p-6 mb-8 !border-2 !border-amber-400" data-testid="alert-codici-sconosciuti">
          <h3 className="font-semibold text-amber-700">
            {report.codici_sconosciuti.length} codici convenzionato sconosciuti da identificare
          </h3>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Questi codici non sono presenti in anagrafica: crea la scheda del convenzionato per
            attribuire correttamente la produzione.
          </p>
          <div className="overflow-x-auto max-h-64">
            <table className="w-full text-sm">
              <thead className="bg-amber-50 text-amber-700 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Codice</th>
                  <th className="text-left px-3 py-2">Ragione sociale nel file</th>
                  <th className="text-right px-3 py-2">Pratiche</th>
                </tr>
              </thead>
              <tbody>
                {report.codici_sconosciuti.map((c) => (
                  <tr key={c.codice_dealer} className="border-t border-amber-100">
                    <td className="px-3 py-2 font-mono">{c.codice_dealer}</td>
                    <td className="px-3 py-2">{c.ragione_sociale || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.pratiche}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Link to="/convenzionati/NON-IDENTIFICATO" data-testid="link-identifica-codici"
            className="inline-block mt-4 px-4 py-2 rounded-md bg-amber-600 text-white text-sm hover:bg-amber-700 transition-colors duration-200">
            Vai a identificare i convenzionati
          </Link>
        </Card>
      )}

      <SectionTitle>Storico import (batch)</SectionTitle>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="batches-table">
          <thead><tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
            <th className="text-left px-6 py-3">Data</th><th className="text-left px-6 py-3">File</th>
            <th className="text-left px-6 py-3">Target</th><th className="text-left px-6 py-3">Utente</th>
            <th className="text-right px-6 py-3">Totali</th><th className="text-right px-6 py-3">Inserite</th>
            <th className="text-right px-6 py-3">Aggiornate</th><th className="text-right px-6 py-3">Scartate</th>
            <th className="text-right px-6 py-3">Annulla</th>
          </tr></thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id} className="border-t border-slate-100">
                <td className="px-6 py-3 font-mono text-slate-500">{dateIt(b.created_at)}</td>
                <td className="px-6 py-3">{b.filename}</td>
                <td className="px-6 py-3">{b.target}</td>
                <td className="px-6 py-3 text-slate-500">{b.user_email}</td>
                <td className="px-6 py-3 text-right font-mono">{num(b.totale_righe)}</td>
                <td className="px-6 py-3 text-right font-mono text-emerald-600">{num(b.righe_importate)}</td>
                <td className="px-6 py-3 text-right font-mono">{num(b.righe_aggiornate)}</td>
                <td className="px-6 py-3 text-right font-mono text-red-600">{num(b.righe_scartate)}</td>
                <td className="px-6 py-3 text-right">
                  {b.annullato ? (
                    <span className="text-xs text-slate-400" title={`Annullato da ${b.annullato_da}`}>
                      annullato
                    </span>
                  ) : (
                    <button onClick={() => annulla(b)} data-testid={`annulla-batch-${b.id}`}
                      disabled={annullando === b.id}
                      className="text-xs font-semibold text-red-600 border border-red-200 rounded px-2 py-1
                        hover:bg-red-50 disabled:opacity-40 transition-colors duration-200">
                      {annullando === b.id ? "Annullo…" : "Annulla import"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="uppercase text-xs tracking-[0.18em] text-slate-500">{label}</div>
    <div className="font-mono text-xl font-semibold">{num(value)}</div>
  </div>
);
